import {useGetPatientAucDoses} from '@/modules/patients/api/use-get-patient-auc-doses'
import {useGetPatientGfrLabs} from '@/modules/patients/api/use-get-patient-gfr-labs'
import {formatDose, KeptDosesAlert} from '@/modules/patients/components/dialogs/kept-doses-alert'
import {formatGfr} from '@/modules/patients/functions'
import {Button, ButtonGroup, Text} from '@chakra-ui/react'
import {Patient} from '@cypro/engine/schema'
import {pickInUseGfr, previewAucDoseChanges, type AucDoseChange} from '@cypro/engine/utils'
import {dialog, DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {DataList} from '@cypro/ui/components/ui/data-list'
import {formatDateKey} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {useCallback} from 'react'

const DIALOG_ID = 'gfr-dose-change'

type AucDose = ReturnType<typeof useGetPatientAucDoses>[0][number]

interface GfrCandidate {
  id: string
  gfr: number
  labTime: number
  createdAt: number | null
  deletedAt: number | null
}

interface Props {
  oldGfr: number | undefined
  newGfr: number | undefined
  changed: AucDoseChange<AucDose>[]
  notChanged: AucDose[]
}

function GfrDoseChangeDialog({oldGfr, newGfr, changed, notChanged}: Props) {
  const {t} = useTranslate('common')

  return (
    <>
      <DialogHeader>{t('gfr.dose_change.title')}</DialogHeader>

      <DialogBody minHeight="none" spaceY="4">
        <Text>{t('gfr.dose_change.description')}</Text>
        <DataList
          items={[
            {
              label: t('gfr.dose_change.gfr_in_use'),
              value: `${formatGfr(oldGfr)} → ${formatGfr(newGfr)} ${t('ml_min')}`,
            },
            {label: t('gfr.dose_change.prescriptions_changed'), value: String(changed.length)},
            ...changed.map(({medicine, oldAmount, newAmount}) => ({
              key: medicine.id,
              label: medicine.therapyDate ? formatDateKey(medicine.therapyDate) : '-',
              value: `${medicine.activeSubstance?.name ?? '-'}: ${formatDose(medicine, oldAmount)} → ${formatDose(medicine, newAmount)}`,
            })),
          ]}
        />
        <KeptDosesAlert
          title={t('gfr.dose_change.not_changed', 'Validated or in production, not changed: {count}', {
            count: notChanged.length,
          })}
          doses={notChanged}
        />
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
          <Button variant="outline" colorPalette="gray" onClick={() => void dialog.close(DIALOG_ID, false)}>
            {t('cancel')}
          </Button>
          <Button onClick={() => void dialog.close(DIALOG_ID, true)}>{t('gfr.dose_change.confirm')}</Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}

/**
 * Before a GFR value is added or deleted: when the change moves the GFR in use and future AUC doses are affected,
 * shows the dose change warning. Resolves to true when the change may be saved.
 */
export function useConfirmGfrDoseChange(patient: Patient) {
  const [gfrLabs] = useGetPatientGfrLabs(patient.id)
  const [aucDoses] = useGetPatientAucDoses(patient.id)
  const characteristics = patient.characteristics?.[0]

  return useCallback(
    async (change: {add: GfrCandidate} | {deleteId: string}) => {
      const nextGfrs: GfrCandidate[] =
        'add' in change ? [...gfrLabs, change.add] : gfrLabs.filter(gfr => gfr.id !== change.deleteId)
      const oldGfr = pickInUseGfr(gfrLabs)?.gfr
      const newGfr = pickInUseGfr(nextGfrs)?.gfr
      if (oldGfr === newGfr) return true

      const {changed, notChanged} = previewAucDoseChanges(aucDoses, characteristics ?? {}, newGfr ?? null)
      if (changed.length === 0 && notChanged.length === 0) return true

      const confirmed: unknown = await dialog.open(DIALOG_ID, {
        children: <GfrDoseChangeDialog oldGfr={oldGfr} newGfr={newGfr} changed={changed} notChanged={notChanged} />,
      })
      return confirmed === true
    },
    [gfrLabs, aucDoses, characteristics]
  )
}
